"use client";

import React from "react";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, FileText, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const heroDetails = [{
    label: "Quarterly reports",
    value: "Q2 2025"
  }, {
    label: "Executive coverage",
    value: "Enterprise & Tech"
  }, {
    label: "Editorial review",
    value: "Peer reviewed"
  }];
  return <section id="hero" data-nav="light" className="relative bg-background text-foreground pt-12 pb-16 sm:pt-16 sm:pb-24 lg:pt-20 lg:pb-28 border-b border-border overflow-hidden">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Masthead rule decorative top bar */}
        <div className="mb-8 sm:mb-12 border-b border-border pb-3 flex flex-wrap items-center justify-between gap-4 text-xs font-mono uppercase text-muted-foreground tracking-wider">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-accent" />
            <span>Independent B2B Research &amp; Market Intelligence</span>
          </div>
          <div className="flex items-center gap-4 hidden sm:flex">
            <span>Volume VIII</span>
            <span className="text-border">|</span>
            <span>ISSN 2490-1120</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Copy Column */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            ease: "easeOut"
          }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wider uppercase mb-6 self-start">
              <FileText className="size-3.5 text-foreground" />
              <span>Editorial &amp; Industry Analysis</span>
            </motion.div>

            <motion.h1 initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45,
            delay: 0.06,
            ease: "easeOut"
          }} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-semibold text-foreground tracking-tight leading-[1.15] mb-6">
              Research, Insights &amp; Resources for the Modern Business Professional
            </motion.h1>

            <motion.p initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45,
            delay: 0.12,
            ease: "easeOut"
          }} className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8">
              Examine rigorous market reports, executive briefings, and comprehensive strategic guides. MinfoMedia delivers verified industry research free from sponsored bias so you can make informed leadership decisions.
            </motion.p>

            {/* Actions */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45,
            delay: 0.18,
            ease: "easeOut"
          }} className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-sm px-7 py-3.5 h-auto text-base font-medium shadow-sm transition-[background-color,box-shadow]">
                <Link to="/ResourcesPage" className="inline-flex items-center gap-2">
                  <span>Explore Resources</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-sm border-primary text-primary hover:bg-primary/5 px-7 py-3.5 h-auto text-base font-medium transition-[background-color,border-color]">
                <Link to="/NewsletterPage">Subscribe</Link>
              </Button>
            </motion.div>

            {/* Key Editorial Signals */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45,
            delay: 0.24,
            ease: "easeOut"
          }} className="mt-10 pt-8 border-t border-border grid grid-cols-3 gap-4 sm:gap-6">
              {heroDetails.map((item, i) => <div key={item.label} data-index={i} className="flex flex-col">
                  <span className="text-[11px] sm:text-xs font-mono uppercase text-muted-foreground">{item.label}</span>
                  <span className="text-xs sm:text-sm font-semibold text-foreground mt-0.5">{item.value}</span>
                </div>)}
            </motion.div>
          </div>

          {/* Visual Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.15,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-md overflow-hidden border border-border bg-card shadow-lg">
              <div className="aspect-[4/3] sm:aspect-[16/11] lg:aspect-[4/5] relative w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/business_analysts_boardroom.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/business_analysts_boardroom.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/business_analysts_boardroom.png-wvc-sizes" alt="Business analysts examining market data charts in a sunlit modern boardroom" loading="eager" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Journal Card Floating Badge */}
              <div className="p-4 sm:p-5 bg-card border-t border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="size-8 rounded-sm bg-muted flex items-center justify-center text-foreground">
                    <BookOpen className="size-4" />
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase text-muted-foreground">Featured Briefing</p>
                    <p className="text-sm font-semibold text-foreground">Annual Executive Market Survey</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
                  <CheckCircle2 className="size-3.5 text-foreground" />
                  <span>Verified</span>
                </div>
              </div>
            </div>

            {/* Subtle paper backdrop card accent */}
            <div aria-hidden="true" className="absolute -bottom-3 -right-3 -z-10 w-full h-full rounded-md border border-border bg-muted/60" />
          </motion.div>
        </div>
      </div>
    </section>;
}